import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { KeyboardScrollView } from "@/components/ui/keyboard-view";

import { spotApi } from "@/api";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import type { OwnerSpot } from "@/types";
import i18n from "@/i18n";
import { shareText } from "@/lib/share-text";

function spotUrl(slug: string): string {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    return `${window.location.origin}/spot/${slug}`;
  }
  return `https://opncue.com/spot/${slug}`;
}

async function copy(text: string): Promise<void> {
  if (
    Platform.OS === "web" &&
    typeof navigator !== "undefined" &&
    navigator.clipboard
  ) {
    await navigator.clipboard.writeText(text);
    return;
  }
  await shareText({ message: text });
}

export default function SpotDashboard() {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [spot, setSpot] = useState<OwnerSpot | null>(null);
  const [loading, setLoading] = useState(true);
  const [welcome, setWelcome] = useState("");
  const [meetups, setMeetups] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const next = await spotApi.dashboard(id);
      setSpot(next);
      setWelcome(next.welcomeNote ?? "");
      setMeetups(next.meetupSpots.join(", "));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!id) return;
    setSaving(true);
    try {
      const next = await spotApi.update(id, {
        welcomeNote: welcome.trim() || null,
        meetupSpots: meetups
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setSpot(next);
    } finally {
      setSaving(false);
    }
  }

  async function copyWith(key: string, text: string) {
    await copy(text);
    setCopied(key);
    setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
  }

  if (loading || !spot) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={styles.centered}>
          <ActivityIndicator color={c.tint} />
        </View>
      </SafeAreaView>
    );
  }

  const url = spotUrl(spot.slug);
  const storyText = i18n.t("business.dashboard.storyText", { url });
  const postText = i18n.t("business.dashboard.postText", {
    venue: spot.venueName,
    count: spot.stats.conversationsStarted,
    url,
  });

  const stats = [
    {
      label: i18n.t("business.dashboard.stats.conversations"),
      value: spot.stats.conversationsStarted,
    },
    {
      label: i18n.t("business.dashboard.stats.groups"),
      value: spot.stats.groupsFormed,
    },
    {
      label: i18n.t("business.dashboard.stats.meetups"),
      value: spot.stats.meetupMoments,
    },
    {
      label: i18n.t("business.dashboard.stats.visible"),
      value: spot.stats.becameVisible,
    },
  ];

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable onPress={() => router.replace("/business")} hitSlop={10}>
          <Ionicons name="chevron-back" size={26} color={c.text} />
        </Pressable>
        <ThemedText variant="bodyStrong" numberOfLines={1}>
          {spot.venueName}
        </ThemedText>
        <View style={{ width: 26 }} />
      </View>

      <KeyboardScrollView contentContainerStyle={styles.content}>
        {spot.status !== "approved" ? (
          <View
            style={[
              styles.banner,
              {
                backgroundColor:
                  spot.status === "rejected" ? c.dangerTint : c.surfaceAlt,
                borderColor: c.border,
              },
            ]}
          >
            <Ionicons
              name={
                spot.status === "rejected"
                  ? "close-circle-outline"
                  : "time-outline"
              }
              size={18}
              color={spot.status === "rejected" ? c.danger : c.tint}
            />
            <ThemedText variant="caption" tone="secondary" style={{ flex: 1 }}>
              {spot.status === "rejected"
                ? i18n.t("business.dashboard.rejected")
                : i18n.t("business.dashboard.pending")}
            </ThemedText>
          </View>
        ) : null}

        <SectionLabel text={i18n.t("business.dashboard.thisWeek")} />
        <View style={styles.statGrid}>
          {stats.map((stat) => (
            <View
              key={stat.label}
              style={[
                styles.statTile,
                { backgroundColor: c.surfaceAlt, borderColor: c.border },
              ]}
            >
              <ThemedText variant="title">{stat.value}</ThemedText>
              <ThemedText variant="caption" tone="secondary">
                {stat.label}
              </ThemedText>
            </View>
          ))}
        </View>

        <SectionLabel text={i18n.t("business.dashboard.welcome")} />
        <TextInput
          value={welcome}
          onChangeText={(t) => setWelcome(t.slice(0, 280))}
          placeholder={i18n.t("business.dashboard.welcomePlaceholder")}
          placeholderTextColor={c.textMuted}
          multiline
          style={[
            styles.textArea,
            {
              color: c.text,
              backgroundColor: c.surface,
              borderColor: c.border,
            },
          ]}
        />

        <SectionLabel text={i18n.t("business.dashboard.meetupSpots")} />
        <TextInput
          value={meetups}
          onChangeText={setMeetups}
          placeholder={i18n.t("business.dashboard.meetupPlaceholder")}
          placeholderTextColor={c.textMuted}
          style={[
            styles.input,
            {
              color: c.text,
              backgroundColor: c.surface,
              borderColor: c.border,
            },
          ]}
        />
        <ThemedText variant="caption" tone="muted">
          {i18n.t("business.dashboard.commaHint")}
        </ThemedText>

        <Pressable
          onPress={save}
          disabled={saving}
          style={[styles.saveBtn, { backgroundColor: c.tint }]}
        >
          <ThemedText variant="bodyStrong" tone="onTint">
            {i18n.t(
              saving ? "business.dashboard.saving" : "business.dashboard.save",
            )}
          </ThemedText>
        </Pressable>

        <SectionLabel text={i18n.t("business.dashboard.shareSpot")} />
        <ShareRow
          label={i18n.t("business.dashboard.spotLink")}
          value={url}
          copied={copied === "link"}
          onCopy={() => copyWith("link", url)}
          c={c}
        />
        <ShareRow
          label={i18n.t("business.dashboard.storyLabel")}
          value={storyText}
          copied={copied === "story"}
          onCopy={() => copyWith("story", storyText)}
          c={c}
        />
        <ShareRow
          label={i18n.t("business.dashboard.postLabel")}
          value={postText}
          copied={copied === "post"}
          onCopy={() => copyWith("post", postText)}
          c={c}
        />

        <Pressable
          onPress={() => void shareText({ message: `${spot.venueName} — ${url}` })}
          style={[styles.shareBtn, { borderColor: c.border }]}
        >
          <Ionicons name="share-social-outline" size={18} color={c.tint} />
          <ThemedText variant="bodyStrong" tone="tint">
            {i18n.t("business.dashboard.sharePage")}
          </ThemedText>
        </Pressable>
      </KeyboardScrollView>
    </SafeAreaView>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <ThemedText variant="label" tone="muted" style={styles.sectionLabel}>
      {text.toUpperCase()}
    </ThemedText>
  );
}

function ShareRow({
  label,
  value,
  copied,
  onCopy,
  c,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  c: ReturnType<typeof useTheme>;
}) {
  return (
    <View
      style={[
        styles.shareRow,
        { backgroundColor: c.surfaceAlt, borderColor: c.border },
      ]}
    >
      <View style={styles.shareCopy}>
        <ThemedText variant="caption" tone="muted">
          {label}
        </ThemedText>
        <ThemedText variant="caption" numberOfLines={2}>
          {value}
        </ThemedText>
      </View>
      <Pressable
        onPress={onCopy}
        style={[styles.copyBtn, { backgroundColor: c.backgroundSelected }]}
      >
        <Ionicons
          name={copied ? "checkmark" : "copy-outline"}
          size={16}
          color={c.tint}
        />
        <ThemedText variant="caption" tone="tint">
          {i18n.t(
            copied ? "business.dashboard.copied" : "business.dashboard.copy",
          )}
        </ThemedText>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  centered: { flex: 1, alignItems: "center", justifyContent: "center" },
  header: {
    minHeight: 56,
    paddingHorizontal: Spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  content: {
    width: "100%",
    maxWidth: 640,
    alignSelf: "center",
    padding: Spacing.xl,
    gap: Spacing.sm,
  },
  banner: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: Spacing.sm,
  },
  sectionLabel: { letterSpacing: 1, marginTop: Spacing.md },
  statGrid: { flexDirection: "row", flexWrap: "wrap", gap: Spacing.sm },
  statTile: {
    flexGrow: 1,
    flexBasis: "45%",
    minWidth: 130,
    gap: 2,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  textArea: {
    minHeight: 76,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    fontSize: 15,
    textAlignVertical: "top",
  },
  input: {
    minHeight: 48,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    fontSize: 15,
  },
  saveBtn: {
    minHeight: 50,
    marginTop: Spacing.sm,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  shareRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  shareCopy: { flex: 1, gap: 2 },
  copyBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.md,
  },
  shareBtn: {
    minHeight: 50,
    marginTop: Spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
});
